import type { MaybeRefOrGetter } from '@vueuse/core';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import type { ValueOf } from '@/helpers/types';
import type { ComputedRef } from 'vue';

import { MonetizationType } from '@/@types/graphql-types';
import { WebLocale } from '@/enums/web-locale';
import { useTitleDetailsStore } from '@/helpers/composables/useStores';
import { toValue } from '@vueuse/core';
import { computed, onMounted, onServerPrefetch } from 'vue';

// TYPES
type digits = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
type day = `${'0'}${Exclude<digits, '0'>}` | `${'1' | '2'}${digits}` | '30' | '31';
type month = `${'0'}${Exclude<digits, '0'>}` | '10' | '11' | '12';
type year = `20${digits}${digits}`;
type ISO8601 = `${year}-${month}-${day}`;

/** Provider promotions are only for titles with Flatrate and free offers of the promo provider. */
const flatrateOrFreeOrAds = [MonetizationType.Flatrate, MonetizationType.Free, MonetizationType.Ads];

interface PromoText {
	/**
	 * Angled ribbon over square provider icons or
	 * drawer below wide provider icons.
	 */
	ribbon?: string;

	/**
	 * Ribbon text exclusively for wide provider icons
	 * which have room for a bit more text.
	 */
	wideRibbon?: string;

	/**
	 * Text below the square provider in GridOffer (next to 4k/quality).
	 * Text above the price in ListOffer.
	 */
	offerText?: string;

	/** Text that appears on the backdrop play button. */
	longText?: string;

	/** Optional promotion start date in `YYYY-MM-DD` format. */
	startDate?: ISO8601;

	/** `YYYY-MM-DD` format. */
	endDate: ISO8601;

	/** 3 character shortname of the promoted provider. */
	provider: string;

	/** The Url of the promoted provider which overrides the regular offer link. */
	url?: string;

	/** Disclaimer text - for additional Terms and Conditions, passed into the DisclaimerButton */
	disclaimerText?: string;

	/** Secondary disclaimer text for BuyBoxOfferNew - for additional Terms and Conditions, passed into the DisclaimerButton */
	offerDisclaimerText?: string;
}

/** Normal PromoText config with the Provider Icon URL */
export type PromoTextWithIcon = PromoText & { icon: string };

// Change uk to gb in the WebLocale list and return it upper case to match store country
type PromotionLocale = Uppercase<Exclude<keyof typeof WebLocale, 'uk'> | 'gb'>;

const promotions = {
	DK: [
		{
			ribbon: 'SALE',
			offerText: '39 kr./md.',
			longText: 'Særtilbud: 39 kr./måned',
			startDate: '2026-01-15',
			endDate: '2026-01-28',
			provider: 'dnp',
			offerDisclaimerText:
				'Tilbuddet slutter 28.01.2026. Kun gyldigt for nye og berettigede tilbagevendende abonnenter. For personer over 18 år. Efter tre måneder fornys abonnementet automatisk til den gældende månedlige pris (fra 59 kr.) medmindre abonnementet afmeldes inden. Betingelser gælder.',
			disclaimerText:
				'Tilbuddet slutter 28.01.2026. Kun gyldigt for nye og berettigede tilbagevendende abonnenter. For personer over 18 år. Efter tre måneder fornys abonnementet automatisk til den gældende månedlige pris (fra 59 kr.) medmindre abonnementet afmeldes inden. Betingelser gælder.',
		},
	],
	DE: [
		{
			ribbon: 'Angebot',
			offerText: 'Ab 4,99 €*',
			longText: 'Ab 4,99 € pro Monat für 3 Monate*',
			startDate: '2026-01-15',
			endDate: '2026-01-28',
			provider: 'dnp',
			offerDisclaimerText:
				'*Aktion endet am 28.01.26. Gilt nur für neue und teilnahmeberechtigte zurückkehrende Abonnenten. Ab 18 Jahren. Nach 3 Monaten wird Disney+ zum Standard mit Werbung-Preis von 6,99 € verlängert, bis du kündigst.',
			disclaimerText:
				'*Aktion endet am 28.01.26. Gilt nur für neue und teilnahmeberechtigte zurückkehrende Abonnenten. Ab 18 Jahren. Nach 3 Monaten wird Disney+ zum Standard mit Werbung-Preis von 6,99 € verlängert, bis du kündigst.',
		},
	],
	ES: [
		{
			ribbon: 'OFERTA',
			offerText: 'DESDE 4,99 € AL MES',
			longText: 'SUSCRÍBETE',
			startDate: '2026-01-15',
			endDate: '2026-01-28',
			provider: 'dnp',
			offerDisclaimerText: 'Después renueva al precio vigente desde 6,99€ al mes salvo cancelación.',
			disclaimerText: 'Después renueva al precio vigente desde 6,99€ al mes salvo cancelación.',
		},
	],
	FI: [
		{
			ribbon: 'ALE',
			offerText: '4,99 € / kk',
			longText: 'Tarjous: 4,99 € / kk',
			startDate: '2026-01-15',
			endDate: '2026-01-28',
			provider: 'dnp',
			offerDisclaimerText:
				'Tarjous päättyy 28.01.26. Tarjous on voimassa vain uusille ja ehdot täyttäville palaaville tilaajille. Tarjous on tarkoitettu vain vähintään 18-vuotiaille. Tilaus uusitaan automaattisesti kolmen kuukauden jälkeen sillä hetkellä voimassa olevaan valitun tilauksen kuukausihintaan (tällä hetkellä alkaen 6,99 €), ellet peru tilausta sitä ennen.',
			disclaimerText:
				'Tarjous päättyy 28.01.26. Tarjous on voimassa vain uusille ja ehdot täyttäville palaaville tilaajille. Tarjous on tarkoitettu vain vähintään 18-vuotiaille. Tilaus uusitaan automaattisesti kolmen kuukauden jälkeen sillä hetkellä voimassa olevaan valitun tilauksen kuukausihintaan (tällä hetkellä alkaen 6,99 €), ellet peru tilausta sitä ennen.',
		},
	],
	GB: [
		{
			ribbon: 'Offer',
			offerText: 'From £3.99*',
			longText: 'From £3.99/mo for 3 months',
			startDate: '2026-01-15',
			endDate: '2026-01-28',
			provider: 'dnp',
			offerDisclaimerText:
				'*Offer ends 28/01/26. New and eligible returning subscribers only. Must be 18+. After 3 months auto-renews at the then-current price of the chosen plan (from £5.99) unless you cancel before then.',
			disclaimerText:
				'*Offer ends 28/01/26. New and eligible returning subscribers only. Must be 18+. After 3 months auto-renews at the then-current price of the chosen plan (from £5.99) unless you cancel before then.',
		},
	],
	IT: [
		{
			ribbon: 'PROMO',
			offerText: 'PROMO',
			longText: 'Da 4,99€/mese per 3 mesi',
			startDate: '2026-01-15',
			endDate: '2026-01-28',
			provider: 'dnp',
			offerDisclaimerText:
				'Offerta valida per utenti con almeno 18 anni senza abbonamento Disney+ attivo. Dopo 3 mesi si autorinnova al prezzo del piano scelto (a partire da 6,99 €) salvo disdetta.',
			disclaimerText:
				'Offerta valida per utenti con almeno 18 anni senza abbonamento Disney+ attivo. Dopo 3 mesi si autorinnova al prezzo del piano scelto (a partire da 6,99 €) salvo disdetta.',
		},
	],
	NO: [
		{
			ribbon: 'SALG',
			offerText: '49 kr./md.',
			longText: 'Supertilbud: 49 kr./måned',
			startDate: '2026-01-15',
			endDate: '2026-01-28',
			provider: 'dnp',
			offerDisclaimerText:
				'Tilbudet gjelder t.o.m. 28.01.26. Gjelder kun for nye og tilbakevendende abonnenter som oppfyller kriteriene. Må være 18+. Etter 3 måneder fornyes abonnementet automatisk til den aktuelle månedlige prisen for det valgte abonnementet (for tiden fra 69 kr), med mindre du avslutter abonnementet før det.',
			disclaimerText:
				'Tilbudet gjelder t.o.m. 28.01.26. Gjelder kun for nye og tilbakevendende abonnenter som oppfyller kriteriene. Må være 18+. Etter 3 måneder fornyes abonnementet automatisk til den aktuelle månedlige prisen for det valgte abonnementet (for tiden fra 69 kr), med mindre du avslutter abonnementet før det.',
		},
	],
	PL: [
		{
			ribbon: 'Oferta',
			longText: '23,99zł/mc przez 3 mc',
			startDate: '2026-01-15',
			endDate: '2026-01-28',
			provider: 'dnp',
			offerDisclaimerText: 'Po 3 mc odnow. w cenie: Standard 34,99 zł, Premium 59,99 zł',
			disclaimerText: 'Po 3 mc odnow. w cenie: Standard 34,99 zł, Premium 59,99 zł',
		},
	],
	SE: [
		{
			ribbon: 'DEAL',
			offerText: '49 kr/mån',
			longText: '49 kr per mån i 3 månader',
			startDate: '2026-01-15',
			endDate: '2026-01-28',
			provider: 'dnp',
			offerDisclaimerText:
				' Erbjudandet gäller t.o.m. 28 januari 2026. Gäller nya/berättigade återkommande abonnenter som är minst 18 år. Efter tre månader förnyas abonnemanget automatiskt till det ordinarie månadspris som gäller vid det tillfället (från 69 kr), om det inte avslutas innan dess. Villkor gäller.',
			disclaimerText:
				' Erbjudandet gäller t.o.m. 28 januari 2026. Gäller nya/berättigade återkommande abonnenter som är minst 18 år. Efter tre månader förnyas abonnemanget automatiskt till det ordinarie månadspris som gäller vid det tillfället (från 69 kr), om det inte avslutas innan dess. Villkor gäller.',
		},
	],
} satisfies Partial<Record<PromotionLocale, PromoText[]>>;

type PromotionCountries = keyof typeof promotions;
type PromotionProviders = ValueOf<typeof promotions>[number]['provider'];

const promotionProviderIcons: Record<PromotionProviders, string> = {
	amp: `${JW_CONFIG.IMAGESCALER_URL}/icon/52449539/s100/amazonprime.webp`,
	prv: `${JW_CONFIG.IMAGESCALER_URL}/icon/52449539/s100/amazonprime.webp`,
	hst: `${JW_CONFIG.IMAGESCALER_URL}/icon/324950024/s100/hotstar.webp`,
	dnp: `${JW_CONFIG.IMAGESCALER_URL}/icon/313118777/s100/disneyplus.webp`,
	atp: `${JW_CONFIG.IMAGESCALER_URL}/icon/152862153/s100/appletvplus.webp`,
	bng: `${JW_CONFIG.IMAGESCALER_URL}/icon/183470172/s100/binge.webp`,
	pmp: `${JW_CONFIG.IMAGESCALER_URL}/icon/242706661/s100/paramountplus.webp`,
	sst: `${JW_CONFIG.IMAGESCALER_URL}/icon/300772362/s100/skyshowtime.webp`,
	mxx: `${JW_CONFIG.IMAGESCALER_URL}/icon/305458112/s100/max.webp`,
	tbv: `${JW_CONFIG.IMAGESCALER_URL}/icon/313528601/s100/tubitv.webp`,
	cts: `${JW_CONFIG.IMAGESCALER_URL}/icon/48600036/s100/curiositystream.webp`,
};

/** SSR Safe way to get active provider promotions. */
export function useProviderPromotion(title?: MaybeRefOrGetter<TitleDetail>): {
	titleHasProviderPromotion: ComputedRef<boolean>;
	isPromotionOffer: (offer: TitleOfferFragment) => boolean;
	providerPromotion: ComputedRef<PromoTextWithIcon>;
} {
	const { setProviderPromotion, providerPromotion } = useTitleDetailsStore();

	onServerPrefetch(async () => await setProviderPromotion(toValue(title)));

	// refetch on client (no hydration error) to get the updated status
	onMounted(async () => await setProviderPromotion(toValue(title)));

	const titleHasProviderPromotion = computed(() => providerPromotion.value != null);

	function isPromotionOffer(offer: TitleOfferFragment) {
		return (
			flatrateOrFreeOrAds.includes(offer.monetizationType) &&
			(providerPromotion.value as PromoTextWithIcon | null)?.provider === offer.package.shortName
		);
	}

	return {
		/** Is a provider promotion applicable to the current title. */
		titleHasProviderPromotion,

		/** Check if the offer is under the current title's provider promotion. */
		isPromotionOffer,

		/** The applicable promotion itself. */
		providerPromotion,
	};
}

export function getPromoText(
	country: string,
	title: TitleDetail | null = null,
	currentDate: Date = new Date()
): PromoTextWithIcon | null {
	const promos = promotions[country as PromotionCountries];

	if (!promos || promos.length === 0 || title == null || title.offers.length === 0) return null;

	const promoText = promos.find(promo =>
		title.offers.some(
			offer => flatrateOrFreeOrAds.includes(offer.monetizationType) && offer.package.shortName === promo.provider
		)
	);

	if (promoText == null) return null;
	// Checking if we're within the promo dates
	if (promoText.startDate && +currentDate < new Date(promoText.startDate).setUTCHours(0, 0, 0, 0)) return null;
	if (promoText.endDate && +currentDate > new Date(promoText.endDate).setUTCHours(23, 59, 59, 999)) return null;

	return { ...promoText, icon: promotionProviderIcons[promoText.provider] };
}

// Disney External Pixel Tracking
const oceaniaLocales = new Set([WebLocale.au, WebLocale.nz]);
const noramLocales = new Set([WebLocale.us, WebLocale.ca]);
const latamLocales = new Set([
	WebLocale.br,
	WebLocale.mx,
	WebLocale.ar,
	WebLocale.cl,
	WebLocale.co,
	WebLocale.pe,
	WebLocale.uy,
	WebLocale.ec,
	WebLocale.pa,
	WebLocale.cr,
	WebLocale.py,
	WebLocale.bo,
	WebLocale.gt,
	WebLocale.ni,
	WebLocale.do,
	WebLocale.sv,
	WebLocale.hn,
	WebLocale.ve,
]);

export function getDisneyImpressionUrl(providerShortName: string | null | undefined, webLocale: WebLocale) {
	if (providerShortName === 'dnp' /* Disney+ */) {
		if (oceaniaLocales.has(webLocale) || noramLocales.has(webLocale))
			return 'https://imp.pxf.io/i/1206980/1833790/9358';

		if (latamLocales.has(webLocale)) return 'https://imp.pxf.io/i/1206980/1848147/9358';
	}

	if (providerShortName === 'srp' /* Star+ */) {
		if (latamLocales.has(webLocale)) return 'https://imp.pxf.io/i/1206980/1584316/13591';
	}

	return null;
}
